import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { api, formatApiError } from "../lib/api";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      // Dev convenience: token returned for testing
      if (data.reset_token) {
        const link = `${window.location.origin}/reset-password?token=${data.reset_token}`;
        setDevLink(link);
      }
      toast.success(t("forgotSent"));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-[calc(100vh-72px)] items-center justify-center px-4 py-16">
      <div className="w-full max-w-md card p-8 reveal" data-testid="forgot-card">
        <Logo size={44} color="hsl(var(--ink))" className="mb-6" />
        <Link to="/login" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-fg hover:text-ink">
          <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" />
          {t("signIn")}
        </Link>
        <h1 className="font-display mt-4 text-4xl">
          {t("forgotTitle")} <span className="font-serif text-accent">?</span>
        </h1>
        <p className="mt-3 text-sm text-muted-fg">{t("forgotSub")}</p>

        {sent ? (
          <div className="mt-6 rounded-2xl border border-accent/30 bg-[hsl(var(--accent-soft))] p-5 text-sm" data-testid="forgot-success">
            <div className="font-semibold text-ink">{t("forgotSent")}</div>
            <div className="mt-1 text-muted-fg">{t("forgotCheckEmail")}</div>
            {devLink && (
              <div className="mt-4 rounded-lg surface-2 p-3 text-xs">
                <div className="mb-1 font-semibold uppercase tracking-wider text-muted-fg">Dev preview link:</div>
                <a href={devLink} className="break-all text-accent hover:underline" data-testid="forgot-dev-link">
                  {devLink}
                </a>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-fg">
                {t("email")}
              </label>
              <div className="relative mt-1.5">
                <Mail className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-fg" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-base ps-10"
                  data-testid="forgot-email"
                />
              </div>
            </div>
            {err && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300" data-testid="forgot-error">{err}</div>}
            <button type="submit" disabled={busy} className="btn btn-accent w-full" data-testid="forgot-submit">
              {busy ? "…" : t("forgotSend")}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
